import * as m from '@staiter/i18n';
import type { Settlement, SettlementLine } from '@staiter/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Breadcrumbs, PageHeader } from '~/components/data';
import { CompanyContextBanner } from '~/components/domain/CompanyContextBanner';
import { Button, DatePicker, Input } from '~/components/primitives';
import {
  type SettlementCreateBody,
  fetchSettlement,
  updateSettlement,
} from '~/lib/api/settlements';
import shared from './_auth._shared.module.css';

/**
 * FE-86 — Settlement edit page (draft only).
 * Меньшая версия чем .new — изменяем дату + примечания. Линии read-only
 * (full-replace на BE, но для UX избегаем сложного line-editor — пересоздать
 * через /new при необходимости изменить структуру).
 */

export const Route = createFileRoute('/_auth/settlements/$id/edit')({
  component: SettlementEdit,
});

function SettlementEdit(): JSX.Element {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['settlement', id],
    queryFn: () => fetchSettlement(id),
  });
  const s = query.data as Settlement | undefined;
  const [date, setDate] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (s) {
      setDate(s.settlement_date);
      setNotes(s.notes_ru ?? '');
    }
  }, [s]);

  const updateMut = useMutation({
    mutationFn: (body: SettlementCreateBody) => updateSettlement(id, body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['settlement', id] });
      navigate({ to: '/settlements/$id', params: { id } });
    },
  });

  if (!s) {
    return (
      <div>
        <PageHeader title={m.phase2_settlements_title()} />
        <div>{query.isLoading ? '…' : 'Not found'}</div>
      </div>
    );
  }

  const readOnly = s.state !== 'draft';

  const submit = () => {
    const body: SettlementCreateBody = {
      settlement_date: date,
      lines: (s.lines ?? []).map((l: SettlementLine, i: number) => ({
        line_no: i + 1,
        direction: l.direction,
        counterparty_id: l.counterparty_id,
        invoice_id: l.invoice_id,
        amount: { amount: l.amount.amount, currency: s.currency },
        currency: s.currency,
      })),
      net_amount: s.net_amount,
      currency: s.currency,
      notes_ru: notes || null,
    };
    updateMut.mutate(body);
  };

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: m.phase2_settlements_title(), to: '/settlements' },
              { label: s.document_number, to: `/settlements/${id}` },
              { label: m.common_button_edit() },
            ]}
          />
        }
        title={`Редактирование · ${s.document_number}`}
      />
      {/* access-control (F9) — контекст-плашка компании на edit-форме. */}
      <CompanyContextBanner />
      {readOnly && (
        <div
          style={{
            padding: 12,
            background: 'var(--warn-bg-15)',
            color: 'var(--color-warn)',
            border: '1px solid var(--color-warn)',
            borderRadius: 6,
            marginBottom: 16,
          }}
        >
          Зачёт в состоянии «{s.state}» нельзя редактировать. Только статус draft.
        </div>
      )}
      <div className={shared.formColumn480}>
        <DatePicker
          label="Дата зачёта"
          value={date}
          onChange={(v) => setDate(v ?? '')}
          disabled={readOnly}
        />
        <Input
          label="Примечание"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={readOnly}
        />
        <div
          style={{
            padding: 12,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            fontSize: 13,
            color: 'var(--text-secondary)',
          }}
        >
          Для изменения позиций ({(s.lines ?? []).length}) пересоздайте взаимозачёт через мастер «+
          Новый зачёт».
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button
            variant="ghost"
            onClick={() => navigate({ to: '/settlements/$id', params: { id } })}
          >
            {m.common_button_cancel()}
          </Button>
          <Button onClick={submit} disabled={readOnly} loading={updateMut.isPending}>
            {m.ui_sohranit()}
          </Button>
        </div>
      </div>
    </div>
  );
}
